import { useState } from "react";
import { api, toast } from "../../App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DatePicker from "@/components/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Edit, Trash2, UserX, UserCheck, Users } from "lucide-react";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const MarkLeftDialog = ({ contact, onClose, onSaved }) => {
  const [date, setDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!date) return toast.error("Left date is required");
    setSaving(true);
    try {
      await api.patch(`/contacts/${contact.id}/left-status`, { is_left: true, left_date: date, left_note: note });
      toast.success(`${contact.first_name} marked as left company`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update contact");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="contact-left-dialog">
        <DialogHeader>
          <DialogTitle>Mark {contact.first_name} {contact.last_name} as Left Company</DialogTitle>
          <DialogDescription>They stay on past records but can't be picked for anything new.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contact-left-date">Left Date <span className="text-red-500">*</span></Label>
            <DatePicker id="contact-left-date" max={todayISO()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="contact-left-date-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact-left-note">Note (optional)</Label>
            <Textarea id="contact-left-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Moved to ABC Steel, use Mike instead" data-testid="contact-left-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="contact-left-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="contact-left-save-btn">Mark as Left</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ContactRow = ({ contact, onEdit, onDelete, canDelete, onMarkLeft, onRestore }) => (
  <TableRow className={contact.is_left ? "opacity-60 bg-gray-50" : ""} data-testid={`contact-row-${contact.id}`}>
    <TableCell className="font-medium">
      {contact.first_name} {contact.last_name}
      {contact.is_left && (
        <div className="mt-1">
          <Badge className="bg-gray-600 text-white text-[10px]" data-testid={`contact-left-badge-${contact.id}`}>Left {contact.left_date}</Badge>
          {contact.left_note && <div className="text-xs text-gray-600 mt-1 font-normal">{contact.left_note}</div>}
        </div>
      )}
    </TableCell>
    <TableCell>{contact.email || "-"}</TableCell>
    <TableCell>
      {contact.business_phone || contact.phone || "-"}
      {contact.business_extension && ` Ext. ${contact.business_extension}`}
    </TableCell>
    <TableCell>{contact.mobile_phone || "-"}</TableCell>
    <TableCell>{contact.designation || "-"}</TableCell>
    <TableCell>
      {contact.roles?.length ? (
        <div className="flex gap-1 flex-wrap">
          {contact.roles.map((role) => <Badge key={role} variant="outline" className="text-xs">{role}</Badge>)}
        </div>
      ) : "-"}
    </TableCell>
    <TableCell>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="rounded-md3-full" onClick={() => onEdit(contact)} data-testid={`contact-edit-btn-${contact.id}`}>
          <Edit className="h-4 w-4" />
        </Button>
        {contact.is_left ? (
          <Button size="sm" variant="outline" className="rounded-md3-full text-green-700" title="Bring back" onClick={() => onRestore(contact)} data-testid={`contact-restore-btn-${contact.id}`}>
            <UserCheck className="h-4 w-4" />
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="rounded-md3-full text-gray-700" title="Mark as left company" onClick={() => onMarkLeft(contact)} data-testid={`contact-mark-left-btn-${contact.id}`}>
            <UserX className="h-4 w-4" />
          </Button>
        )}
        {canDelete && (
          <Button size="sm" variant="destructive" className="bg-red-600 hover:bg-red-700 text-white rounded-md3-full" onClick={() => onDelete(contact.id)} data-testid={`contact-delete-btn-${contact.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </TableCell>
  </TableRow>
);

export const ContactsTable = ({ contacts, onEdit, onDelete, canDelete, onChanged }) => {
  const [showLeft, setShowLeft] = useState(false);
  const [marking, setMarking] = useState(null);
  const leftCount = contacts.filter((c) => c.is_left).length;
  const visible = showLeft ? contacts : contacts.filter((c) => !c.is_left);
  const restore = async (contact) => {
    try {
      await api.patch(`/contacts/${contact.id}/left-status`, { is_left: false });
      toast.success(`${contact.first_name} is active again`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update contact");
    }
  };
  return (
    <div className="space-y-3">
      {leftCount > 0 && (
        <div className="flex items-center gap-2 justify-end">
          <Switch id="show-left-contacts" checked={showLeft} onCheckedChange={setShowLeft} data-testid="contacts-show-left-switch" />
          <Label htmlFor="show-left-contacts" className="text-sm text-gray-700">Show left contacts ({leftCount})</Label>
        </div>
      )}
      {visible.length === 0 ? (
        <div className="text-center py-8 text-md3-on-surface-variant" data-testid="contacts-empty">
          <Users className="mx-auto h-12 w-12 mb-2 text-gray-400" />
          <p>{contacts.length ? "All contacts for this record have left. Turn on 'Show left contacts' to see them." : "No contacts yet. Add the first contact!"}</p>
        </div>
      ) : (
        <Table className="bg-md3-surface-container rounded-md3-md">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Business Phone</TableHead>
              <TableHead>Mobile Phone</TableHead>
              <TableHead>Designation</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((c) => (
              <ContactRow key={c.id} contact={c} onEdit={onEdit} onDelete={onDelete} canDelete={canDelete} onMarkLeft={setMarking} onRestore={restore} />
            ))}
          </TableBody>
        </Table>
      )}
      {marking && <MarkLeftDialog contact={marking} onClose={() => setMarking(null)} onSaved={onChanged} />}
    </div>
  );
};

export const pickableContacts = (contacts, role, selectedId) =>
  contacts.filter((c) => c.roles?.includes(role) && (!c.is_left || c.id === selectedId));

export const contactLabel = (c) =>
  `${c.first_name} ${c.last_name}${c.designation ? ` (${c.designation})` : ""}${c.is_left ? " — Left" : ""}`;
